import React from 'react';
import { Lightbulb, FlaskConical, Award, Rocket, Users, ArrowRight, MapPin, Calendar, Target, Compass, Shield } from 'lucide-react';

// Core Principles - Purpose, Direction, Principles
const corePrinciples = [
  {
    icon: Target,
    title: 'Purpose',
    headline: 'Reliable Power for Critical Operations',
    description: 'We develop battery systems for environments where failure is not acceptable. Our technology is engineered to meet the demands of industrial duty cycles, infrastructure resilience, and mission-critical deployments where uptime and safety are non-negotiable.',
  },
  {
    icon: Compass,
    title: 'Long-Term Direction',
    headline: 'Scaled Manufacturing, Proven Performance',
    description: 'Our roadmap prioritizes validated performance over speculative claims. We are building manufacturing capacity to deliver Multi-Ion battery systems at scale, with each deployment informing continuous improvements in reliability and cost efficiency.',
  },
  {
    icon: Shield,
    title: 'Operating Principles',
    headline: 'Engineering Discipline, Safety First',
    description: 'Every design decision is governed by safety margins, thermal stability, and operational constraints. We maintain rigorous testing protocols, transparent performance data, and clear documentation for procurement and compliance review.',
  },
];

const milestones = [
  {
    year: '2014',
    title: 'Founded',
    description: 'Gegadyne Energy established in Mumbai',
    icon: Lightbulb,
    color: 'emerald'
  },
  {
    year: '2017',
    title: 'R&D Breakthrough',
    description: 'Multi-Ion technology proof of concept',
    icon: FlaskConical,
    color: 'cyan'
  },
  {
    year: '2019',
    title: 'Patents Filed & Granted',
    description: 'Key IP protection secured',
    icon: Award,
    color: 'amber'
  },
  {
    year: '2022',
    title: 'Commercial Sales',
    description: 'First commercial products launched',
    icon: Rocket,
    color: 'emerald'
  },
  {
    year: '2024',
    title: 'Customer Deployments',
    description: 'Commercial deployment with real-world customers',
    icon: Users,
    color: 'cyan'
  },
];

const AboutSection = () => {
  return (
    <section id="about" className="relative py-16 sm:py-20 lg:py-24 bg-gray-950 overflow-hidden" data-testid="about-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
            <MapPin className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-xs sm:text-sm font-semibold uppercase tracking-wider">About Us</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Pioneering the Future of
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent"> Energy Storage</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Founded in Mumbai, Gegadyne Energy is developing next-generation Multi-Ion battery technology 
            to power industrial applications with faster charging, longer life, and superior safety.
          </p>
        </div>

        {/* Main Content - Two Column */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Left - Company Image & Stats */}
          <div className="space-y-6">
            {/* HQ Image */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_power-solutions-72/artifacts/n1szh976_image.png"
                alt="Gegadyne Energy Headquarters"
                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-emerald-400 text-sm font-semibold mb-1">Headquarters</p>
                <p className="text-white font-medium">Mumbai, India</p>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 bg-gray-800/30 rounded-xl border border-white/10 text-center">
                <p className="text-3xl font-bold text-emerald-400 mb-1">10+</p>
                <p className="text-gray-400 text-sm">Years of R&D</p>
              </div>
              <div className="p-5 bg-gray-800/30 rounded-xl border border-white/10 text-center">
                <p className="text-2xl font-bold text-cyan-400 mb-1">Multiple</p>
                <p className="text-gray-400 text-sm">Patents Filed & Granted</p>
              </div>
              <div className="p-5 bg-gray-800/30 rounded-xl border border-white/10 text-center">
                <p className="text-3xl font-bold text-amber-400 mb-1">50K+</p>
                <p className="text-gray-400 text-sm">Cells Produced</p>
              </div>
              <div className="p-5 bg-gray-800/30 rounded-xl border border-white/10 text-center">
                <p className="text-3xl font-bold text-emerald-400 mb-1">UN 38.3</p>
                <p className="text-gray-400 text-sm">Certified</p>
              </div>
            </div>
          </div>

          {/* Right - Mission & Vision */}
          <div className="space-y-6">
            <div className="p-6 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-2xl border border-emerald-500/20">
              <h3 className="text-xl font-bold text-white mb-3">Our Mission</h3>
              <p className="text-gray-300 leading-relaxed">
                To accelerate the transition to sustainable energy by developing battery technology 
                that outperforms lithium-ion in every metric that matters for industrial applications — 
                charging speed, cycle life, temperature tolerance, and total cost of ownership.
              </p>
            </div>

            <div className="p-6 bg-gray-800/30 rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-3">Why Multi-Ion?</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Traditional lithium-ion batteries were designed for consumer electronics, not industrial workhorses. 
                Our Multi-Ion technology is engineered from the ground up for demanding industrial environments.
              </p>
              <ul className="space-y-2">
                {['15-minute fast charging', '5,000+ cycle life', '-40°C to 65°C operation', 'Inherently safe chemistry'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <a 
              href="#contact"
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
            >
              Get in touch with our team
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Core Principles - Three Block Layout */}
        <div className="mb-20">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {corePrinciples.map((principle, index) => (
              <div 
                key={index} 
                className="relative p-8 bg-gray-900/50 border border-gray-800 rounded-lg hover:border-gray-700 transition-colors"
              >
                {/* Icon */}
                <div className="w-12 h-12 mb-6 flex items-center justify-center rounded bg-gray-800 border border-gray-700">
                  <principle.icon className="w-6 h-6 text-gray-400" strokeWidth={1.5} />
                </div>
                
                {/* Label */}
                <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">
                  {principle.title}
                </p>
                
                {/* Headline */}
                <h3 className="text-lg font-semibold text-white mb-4 leading-snug">
                  {principle.headline}
                </h3>
                
                {/* Divider */}
                <div className="w-8 h-px bg-gray-700 mb-4" />
                
                {/* Description */}
                <p className="text-gray-400 text-sm leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <Calendar className="w-5 h-5 text-emerald-400" />
            <h3 className="text-xl font-bold text-white">Our Journey</h3>
          </div>
          
          {/* Timeline - Horizontal on desktop, vertical on mobile */}
          <div className="relative">
            {/* Desktop Timeline */}
            <div className="hidden md:block">
              {/* Timeline Line */}
              <div className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500/50 via-cyan-500/50 to-emerald-500/50" />
              
              {/* Timeline Items */}
              <div className="grid grid-cols-5 gap-4">
                {milestones.map((milestone, index) => (
                  <div key={index} className="relative pt-12">
                    {/* Dot */}
                    <div className={`absolute top-6 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-${milestone.color}-500 border-4 border-gray-950`} />
                    
                    {/* Content */}
                    <div className="text-center">
                      <p className={`text-${milestone.color}-400 font-bold text-lg mb-1`}>{milestone.year}</p>
                      <h4 className="text-white font-semibold text-sm mb-1">{milestone.title}</h4>
                      <p className="text-gray-500 text-xs">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Timeline */}
            <div className="md:hidden space-y-6">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full bg-${milestone.color}-500/20 flex items-center justify-center border border-${milestone.color}-500/50`}>
                      <milestone.icon className={`w-5 h-5 text-${milestone.color}-400`} />
                    </div>
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 h-full bg-gradient-to-b from-white/20 to-transparent mt-2" />
                    )}
                  </div>
                  <div className="pb-6">
                    <p className={`text-${milestone.color}-400 font-bold text-sm`}>{milestone.year}</p>
                    <h4 className="text-white font-semibold">{milestone.title}</h4>
                    <p className="text-gray-500 text-sm">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
